import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { heroActions } from '../Store/Actions/heroActions';
import { getHeroGold, getHeroInventory } from '../Store/Selectors/selectors';
import { ITEMS_LIST } from '../Classes/utils/constants';
import { checkInventoryStackcountByItemId } from '../Helpers/utils';
import { Btn, Portrait, Label, Num, Rule, Coin } from './Common/OkoylUI';

const ShopBox = ({ currentShop }) => {
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const inventory = useSelector(getHeroInventory);
    const gold = useSelector(getHeroGold);

    const [currentItem, setCurrentItem] = useState(null);
    const [quantityItem, setQuantityItem] = useState(1);
    const [currentDialogue, setCurrentDialogue] = useState(currentShop?.shopkeeperData?.dialogues?.welcome || '');

    if (!currentShop) return null;

    const handleQuantityChange = (event) => {
        const value = parseInt(event.target.value, 10);
        setQuantityItem(isNaN(value) ? 1 : Math.max(1, value));
    };

    const buyItem = () => {
        if (!currentItem) return;
        const priceItems = currentItem.price * quantityItem;
        const dialogues = currentShop.shopkeeperData.dialogues;

        if (gold < priceItems) {
            setCurrentDialogue(dialogues.notEnoughGold);
        } else if (checkInventoryStackcountByItemId(inventory, currentItem.id, quantityItem)) {
            dispatch(heroActions.updateGold(-priceItems));
            dispatch(heroActions.buyItem(currentItem.id, quantityItem, -priceItems));
            setCurrentDialogue(dialogues.saleSuccess);
        } else {
            setCurrentDialogue(dialogues.inventoryFull);
        }
    };

    const portraitSrc = currentShop.shopkeeperData?.portrait || '/assets/npcs/shopkeeper.png';

    return (
        <div className="okcard" style={{ width: '100%', maxWidth: 1080 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '300px 1fr', gap: 24, alignItems: 'start' }}>
                {/* NPC */}
                <div>
                    <Portrait src={portraitSrc} name={currentShop.name || t('shop.merchantSub')} sub={t('shop.merchantSub')} tint="gold" />
                    <div className="okcard" style={{ marginTop: 14, padding: 14 }}>
                        <p className="oklore">{currentDialogue || t('shop.welcomeFallback')}</p>
                    </div>
                    <div style={{ marginTop: 14, display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', background: 'var(--ash-900)', borderRadius: 'var(--r-md)', boxShadow: 'inset 0 0 0 1px rgba(200,144,42,0.18)' }}>
                        <Label color="gold"><Coin /> &nbsp;{t('shop.purseLabel')}</Label>
                        <Num size="lg" color="var(--okoyl-200)">{gold}</Num>
                    </div>
                </div>

                {/* ITEMS */}
                <div>
                    <Label color="gold">{t('shop.stallLabel')}</Label>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 12 }}>
                        {currentShop.items.map(itemId => {
                            const item = ITEMS_LIST[itemId];
                            if (!item) return null;
                            const isHovered = currentItem?.id === item.id;
                            return (
                                <div
                                    key={itemId}
                                    onMouseEnter={() => { setCurrentItem(item); setQuantityItem(1); }}
                                    className={`okcard ${isHovered ? 'okcard-on' : 'okcard-tap'}`}
                                    style={{ padding: 12 }}
                                >
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                        <span style={{ fontSize: 32, color: 'var(--okoyl-200)' }}>{item.icon}</span>
                                        <div style={{ flex: 1, minWidth: 0 }}>
                                            <div style={{ fontFamily: 'var(--font-display)', fontSize: 13, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--okoyl-200)' }}>
                                                {item.name}
                                            </div>
                                            <div className="oklore oklore-sm" style={{ marginTop: 2 }}>
                                                {item.description}
                                            </div>
                                        </div>
                                        <div style={{ textAlign: 'right' }}>
                                            <Num size="md" color="var(--okoyl-200)">{item.price}</Num>
                                            <Coin />
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    {currentItem && (
                        <div className="okcard" style={{ marginTop: 18 }}>
                            <Label color="gold">{t('shop.buyTitle', { name: currentItem.name })}</Label>
                            <Rule thin />
                            <div style={{ display: 'flex', gap: 14, alignItems: 'center', marginTop: 8 }}>
                                <span style={{ fontSize: 36 }}>{currentItem.icon}</span>
                                <div style={{ flex: 1 }}>
                                    <p className="oklore oklore-sm">{currentItem.description}</p>
                                </div>
                                <div style={{ textAlign: 'right' }}>
                                    <Label>{t('shop.quantity')}</Label>
                                    <input
                                        type="number"
                                        min="1"
                                        value={quantityItem}
                                        onChange={handleQuantityChange}
                                        className="okinput"
                                        style={{ width: 80, textAlign: 'center', marginTop: 4 }}
                                    />
                                </div>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 14 }}>
                                <span style={{ fontFamily: 'var(--font-mono)', fontSize: 14, color: 'var(--okoyl-200)' }}>
                                    {t('shop.total')} <Num size="md" color="var(--okoyl-200)">{currentItem.price * quantityItem}</Num> <Coin />
                                </span>
                                <Btn variant="primary" onClick={buyItem}>{t('shop.buy')}</Btn>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ShopBox;
